import {
	ApiClient,
	platformService,
	materializeResponseHook,
} from '@monthio/react-components'
import { mutate } from 'swr'
import {
	BankDto,
	BankServiceProviders,
} from '../../app/Modules/Bank/Bank.types'
import { GetAllBankSessionsResponseDto } from '../../app/Modules/Bank/Bank.types'
import { BankLoginUrlDto } from '../../app/Modules/Bank/Bank.types'
import { ConsumerAuthenticationDto } from '../../app/Modules/Bank/Bank.types'
import { RegisterSessionAndGetConsentInput } from '../../app/Modules/Bank/Bank.types'

export class TransactionApiClient {
	private apiClient: ApiClient = null as unknown as ApiClient

	private apiClientWithConsent: ApiClient = null as unknown as ApiClient
	private tokenWithConsent: string = null as unknown as string
	private giveIsInTransit: Promise<void> | false = false
	public initialize = () => {
		this.apiClient = platformService.createApiClient(
			platformService.getAppUrl('transactionApp'),
			{
				appendApi: true,
			},
		)
		this.apiClientWithConsent = platformService.createApiClient(
			platformService.getAppUrl('transactionApp'),
			{
				appendApi: true,
				hooks: {
					beforeError: [materializeResponseHook],
					beforeRequest: [
						async (request) => {
							this.tokenWithConsent = await this.getTokenWithConsent()
							request.headers.set(
								'Authorization',
								`Bearer ${this.tokenWithConsent}`,
							)
						},
					],
					beforeRetry: [
						async ({ retryCount }) => {
							if (retryCount === 0) {
								this.tokenWithConsent = await this.getTokenWithConsent(true)
							}
						},
					],
				},
			},
		)

		mutate(
			`/api/banks`,
			async () => {
				const allBanks = await this.getBanks()

				return allBanks
			},
			false,
		)
	}

	public getBanks = async () => {
		let path = 'banks?$skippages=0&$pagesize=2500'

		if (isDemoProfile()) {
			path = `${path}&$filter=serviceProviderId eq ${BankServiceProviders.DemoProfiles}`
		} else {
			path = `${path}&$filter=serviceProviderId ne ${BankServiceProviders.DemoProfiles}`
		}

		return this.apiClient
			.timeout(false)
			.retry(3)
			.useDeferred()
			.get<BankDto[]>(path)
	}

	public preloadBank = (bankId: string | number) => {
		mutate(`/api/banks/${bankId}`, async () => {
			const bank = await this.getBank(bankId)
			return bank
		})
	}

	public getBank = async (bankId: string | number) => {
		return this.apiClient.useDeferred().get<BankDto>(`banks/${bankId}`)
	}

	public createPsd2Consent = async () => {
		let resolveInTransit: () => void = noop
		this.giveIsInTransit = new Promise((resolve) => {
			resolveInTransit = resolve
		})
		try {
			const response = await this.apiClient.post<string>('psd2-consent/give')
			this.tokenWithConsent = response
			resolveInTransit()
			return response
		} catch (error) {
			resolveInTransit()
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public getBankSessions = async () => {
		try {
			const response = await this.apiClientWithConsent
				.retry(2)
				.get<GetAllBankSessionsResponseDto>(
					'consumer-data-pull-sessions/bank-sessions',
				)

			return response
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public createBankLoginUrl = async (bankId: number, ssn?: string) => {
		try {
			const response = await this.apiClientWithConsent.post<BankLoginUrlDto>(
				'consumer-data-pull-sessions/bank-login-urls',
				{
					bankId,
					redirectUrl: `${platformService.getClientUrl(
						'smartcheckFlowClient',
					)}/redirect-receiver`,
					ssn,
				},
			)

			return response
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public registerSessionAndGetConsent = async () => {
		try {
			const input = {
				ConsentType: 'transactionsAndAccounts',
			} as RegisterSessionAndGetConsentInput

			const response = await this.apiClient
				.useDeferred()
				.useCredentials('include')
				.put<ConsumerAuthenticationDto>(
					`consumer-data-pull-sessions/register-and-get-consent`,
					input,
				)
			const tokenHasPsd2Consent = jwtHasPsd2ConsentGiven(response.token)
			if (tokenHasPsd2Consent) {
				this.tokenWithConsent = response.token
			}
			return response
		} catch (error: any) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public reopenConsumerDataPullSession = async () => {
		try {
			await this.apiClientWithConsent.post<void>(
				'consumer-data-pull-sessions/reopen',
			)
		} catch (error) {
			const mappedError = this.apiClient.mapError(error)
			const isPAPullSession1Error = mappedError.data.errors.every(
				(e: any) => e.errorCode === 'PA_PullSession_1',
			)
			if (isPAPullSession1Error) {
				return mappedError.data
			}
			return mappedError
		}
	}

	public closeConsumerDataPullSession = async (
		deselectedAccounts: string[],
	) => {
		try {
			await this.apiClientWithConsent.post<void>(
				'consumer-data-pull-sessions/close',
				{
					DeselectedByUserAccounts: deselectedAccounts,
				},
			)
			return true
		} catch (error) {
			await this.apiClientWithConsent.post<void>(
				'consumer-data-pull-sessions/reopen',
			)
		}
	}

	public getTokenWithConsent = async (force = false): Promise<string> => {
		if (this.giveIsInTransit !== false) {
			await this.giveIsInTransit
		}
		const tokenHasPsd2Consent = jwtHasPsd2ConsentGiven(this.tokenWithConsent)
		if (tokenHasPsd2Consent && !force) {
			return this.tokenWithConsent
		}
		const auth = await this.registerSessionAndGetConsent()

		return auth.token
	}
}

export const transactionApiClient = new TransactionApiClient()
const noop = () => undefined

const parseJwt = (token?: string) => {
	if (token == null) {
		return null
	}
	const base64Url = token.split('.')[1]
	if (base64Url == null) {
		return null
	}
	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
	const jsonPayload = decodeURIComponent(
		window
			.atob(base64)
			.split('')
			.map(function (c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
			})
			.join(''),
	)

	return JSON.parse(jsonPayload)
}

const jwtHasPsd2ConsentGiven = (token?: string) => {
	const jwt = parseJwt(token)
	return jwt?.PSD2ConsentGiven === 'true'
}

const getQueryStringParameters = (qs: string) => {
	qs = qs.split('+').join(' ')

	const params: Record<string, any> = {}
	let tokens: RegExpExecArray | null
	const re = /[?&]?([^=]+)=([^&]*)/g

	while ((tokens = re.exec(qs))) {
		params[decodeURIComponent(tokens[1] ?? '').toLowerCase()] =
			decodeURIComponent(tokens[2] ?? '')
	}
	return params
}

const resolveSessionId = () => {
	const queryStringParams = getQueryStringParameters(document.location.search)
	const sessionId = queryStringParams.sessionid ?? queryStringParams.parringid

	return sessionId
}

const isDemoProfile = () => {
	const queryStringParams = getQueryStringParameters(document.location.search)
	return queryStringParams.demoprofile !== undefined
}
